import { ASSETS, buildWhatsAppLink } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";
import { Building2, Heart, Users, Gift, Sparkles, MessageCircle } from "lucide-react";

const occasions = [
  {
    icon: Building2,
    title: "Corporate Gifts",
    text: "Branded boxes for clients, partners, and team celebrations — packaged with corporate elegance.",
    image: "https://images.unsplash.com/photo-1607920591413-e9b62b8be656?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Heart,
    title: "Weddings",
    text: "Wedding favours and dessert tables curated to match your colors and theme.",
    image: ASSETS.giftBox2,
  },
  {
    icon: Users,
    title: "Family Occasions",
    text: "Generous platters of nuts, dates, and chocolates for the moments that bring everyone together.",
    image: "https://images.unsplash.com/photo-1607920592519-bab2a4e0d362?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Sparkles,
    title: "Holidays",
    text: "Eid, Ramadan, Christmas — seasonal collections designed to celebrate every tradition.",
    image: ASSETS.giftBox,
  },
  {
    icon: Gift,
    title: "Special Events",
    text: "Birthdays, anniversaries, baby showers — bespoke gifting for life's milestones.",
    image: "https://images.unsplash.com/photo-1545569310-a90fdc5b3a3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export default function Gifting() {
  const ref = useReveal();
  return (
    <div ref={ref} data-testid="gifting-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[65vh] min-h-[460px] overflow-hidden">
        <img src={ASSETS.giftBox} alt="Luxury gift box" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-brand-ivory px-6">
          <span className="text-[11px] tracking-[0.32em] uppercase text-brand-gold animate-fade-in-up">Gifting</span>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-6xl lg:text-7xl animate-fade-in-up" style={{animationDelay:"120ms"}}>
            Exquisite gifts,<br/><span className="text-brand-gold">memorable moments.</span>
          </h1>
          <p className="mt-5 max-w-xl text-brand-ivory/85 animate-fade-in-up" style={{animationDelay:"240ms"}}>
            Beautifully composed gift boxes for every occasion — personalized to feel exactly like you.
          </p>
          <a
            href={buildWhatsAppLink("Hello! I'd like to design a custom gift box.")}
            target="_blank" rel="noreferrer"
            data-testid="gifting-hero-whatsapp"
            className="mt-8 inline-flex items-center gap-2 bg-brand-gold text-brand-brown px-7 py-3.5 rounded-full font-semibold hover:bg-[#E5C158] transition-all hover:shadow-2xl animate-fade-in-up"
            style={{animationDelay:"360ms"}}
          >
            <MessageCircle className="h-4 w-4" /> Plan Your Gift
          </a>
        </div>
      </section>

      {/* Occasions bento */}
      <section className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <span className="text-[11px] tracking-[0.28em] uppercase text-brand-gold font-semibold">
              Occasions We Curate For
            </span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-brand-brown">
              Every moment deserves a thoughtful box.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {occasions.map((o, i) => (
              <article
                key={o.title}
                data-testid={`occasion-card-${i}`}
                className="reveal group relative rounded-3xl overflow-hidden bg-brand-brown text-brand-ivory shadow-lg hover:shadow-2xl transition-shadow"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={o.image} loading="lazy" alt={o.title} className="zoom-img w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3B1E1A]/80 via-[#3B1E1A]/20 to-transparent" />
                  <div className="absolute top-4 left-4 h-10 w-10 rounded-full bg-brand-gold/95 text-brand-brown flex items-center justify-center">
                    <o.icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl">{o.title}</h3>
                  <p className="mt-2 text-sm text-brand-ivory/75 leading-relaxed">{o.text}</p>
                  <a
                    href={buildWhatsAppLink(`Hello! I'd like a gifting suggestion for ${o.title}.`)}
                    target="_blank" rel="noreferrer"
                    data-testid={`occasion-whatsapp-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-brand-gold text-sm hover:text-[#E5C158]"
                  >
                    Enquire on WhatsApp →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Custom box CTA */}
      <section className="py-20 md:py-28 bg-brand-beige/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal rounded-3xl overflow-hidden">
            <img src={ASSETS.giftBox2} alt="Custom gift box" className="zoom-img w-full h-full object-cover aspect-[4/3]" />
          </div>
          <div className="reveal">
            <span className="text-[11px] tracking-[0.28em] uppercase text-brand-gold font-semibold">Bespoke Boxes</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-brand-brown leading-tight">
              Tell us the occasion. We&apos;ll do the rest.
            </h2>
            <p className="mt-4 text-brand-muted">
              Choose your favourites, your color palette, your budget — we&apos;ll handcraft a gift that
              feels considered and unmistakably yours.
            </p>
            <a
              href={buildWhatsAppLink("Hello! I'd like to design a custom gift box.")}
              target="_blank" rel="noreferrer"
              data-testid="custom-gift-cta"
              className="mt-7 inline-flex items-center gap-2 bg-brand-brown text-brand-ivory px-7 py-3.5 rounded-full font-medium hover:bg-[#5A2F2A] transition-all hover:shadow-xl"
            >
              <MessageCircle className="h-4 w-4 text-brand-gold" />
              Start Designing on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
